import { useEffect, useState, type FormEvent } from "react";
import {
  ClipboardCopy,
  Mail,
  Pencil,
  Save,
  Shield,
  Sparkles,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import { api, ApiError, type LiabilityPolicy } from "../api";
import { useAuth } from "../auth";
import { Button, Card, Field, PageHeader, cx } from "../ui";

type Unit = { id: string; unitNumber: string; buildingName?: string };

type RenewalView = {
  upcomingRenewals: Array<{
    unitId: string;
    unitNumber: string;
    buildingName: string;
    policyId: string | null;
    carrierName: string | null;
    policyNumber: string | null;
    expirationDate: string | null;
    daysUntilExpiration: number | null;
    urgent: boolean;
    renewalDraft: string;
  }>;
  carrierSuggestions: Array<{ carrierName: string; policyCount: number }>;
  coiRequirements: string[];
};

export function MyInsurancePage() {
  const { state } = useAuth();
  const [policies, setPolicies] = useState<LiabilityPolicy[] | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [renewal, setRenewal] = useState<RenewalView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    unitId: "",
    carrierName: "",
    policyNumber: "",
    limitDollars: "500000",
    effectiveDate: "",
    expirationDate: "",
  });
  // Non-null when the form is editing an existing policy. Switches
  // the submit handler to PATCH and disables the unit picker (we
  // don't allow moving a policy across units — that's a delete-
  // and-recreate).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  const reload = async () => {
    const r = await api.get<{ data: LiabilityPolicy[] }>(
      "/api/my/liability-policies",
    );
    setPolicies(r.data);
    try {
      const view = await api.get<RenewalView>("/api/my/insurance/renewal");
      setRenewal(view);
    } catch {
      setRenewal(null);
    }
  };

  useEffect(() => {
    void reload();
    // Only the units this owner is actually on the deed for. The
    // building's full unit list is irrelevant — owners can't file an
    // insurance policy for a unit they don't own.
    void api
      .get<{ data: Unit[] }>("/api/my/units")
      .then((r) => {
        setUnits(r.data);
        if (r.data.length === 1 && r.data[0]) {
          // Single-owner case: pre-select to save a click.
          setForm((f) => ({ ...f, unitId: r.data[0]!.id }));
        }
      })
      .catch(() => setUnits([]));
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      unitId: units.length === 1 && units[0] ? units[0].id : "",
      carrierName: "",
      policyNumber: "",
      limitDollars: "500000",
      effectiveDate: "",
      expirationDate: "",
    });
  };

  const startEdit = (p: LiabilityPolicy) => {
    setError(null);
    setEditingId(p.id);
    setForm({
      unitId: p.unitId,
      carrierName: p.carrierName,
      policyNumber: p.policyNumber,
      limitDollars: String(Number(p.perIncidentLimitCents) / 100),
      effectiveDate: p.effectiveDate,
      expirationDate: p.expirationDate,
    });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }
  };

  const removePolicy = async (p: LiabilityPolicy) => {
    if (
      typeof window !== "undefined" &&
      !window.confirm(
        `Delete ${p.carrierName} · ${p.policyNumber}? This cannot be undone.`,
      )
    ) {
      return;
    }
    setError(null);
    setRowBusyId(p.id);
    try {
      await api.del(`/api/my/liability-policies/${p.id}`);
      if (editingId === p.id) resetForm();
      await reload();
    } catch (err) {
      if (err instanceof ApiError) setError(JSON.stringify(err.body));
      else setError(String(err));
    } finally {
      setRowBusyId(null);
    }
  };

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    setError(null);
    setBusy(true);
    try {
      if (editingId) {
        const cents = Math.round(parseFloat(form.limitDollars) * 100);
        await api.patch(`/api/my/liability-policies/${editingId}`, {
          carrierName: form.carrierName,
          policyNumber: form.policyNumber,
          perIncidentLimitCents: String(cents),
          effectiveDate: form.effectiveDate,
          expirationDate: form.expirationDate,
        });
      } else {
        const fd = new FormData();
        fd.append("unitId", form.unitId);
        fd.append("carrierName", form.carrierName);
        fd.append("policyNumber", form.policyNumber);
        const cents = Math.round(parseFloat(form.limitDollars) * 100);
        fd.append("perIncidentLimitCents", String(cents));
        fd.append("effectiveDate", form.effectiveDate);
        fd.append("expirationDate", form.expirationDate);
        const fileInput = formEl.elements.namedItem(
          "file",
        ) as HTMLInputElement | null;
        const file = fileInput?.files?.[0];
        if (file) fd.append("file", file);

        const res = await fetch("/api/my/liability-policies", {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
            message?: string;
          };
          throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
        }
      }
      formEl.reset();
      resetForm();
      await reload();
    } catch (err) {
      if (err instanceof ApiError) setError(JSON.stringify(err.body));
      else setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  if (state.status !== "authenticated") return null;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="My liability insurance"
        subtitle="Per the Construction Rules, a $500,000 personal liability policy must be on file before any non-emergency project will be approved. A missing certificate triggers an automatic $100/month fine on the 15th of each month."
      />

      <RenewalNudge renewal={renewal} />

      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
          <Shield size={18} /> On file
        </h2>
        {policies === null ? (
          <p className="text-ink-500">Loading…</p>
        ) : policies.length === 0 ? (
          <p className="text-ink-500 text-sm">No policies on file.</p>
        ) : (
          <ul className="divide-y divide-ink-100">
            {policies.map((p) => {
              const expires = new Date(p.expirationDate);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const daysToExpiry = Math.ceil(
                (expires.getTime() - today.getTime()) / 86_400_000,
              );
              const expired = daysToExpiry < 0;
              const expiringSoon = !expired && daysToExpiry <= 30;
              return (
                <li
                  key={p.id}
                  className="py-3 flex items-center gap-3 flex-wrap"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink-800 truncate">
                      {p.carrierName} · {p.policyNumber}
                    </div>
                    <div className="text-xs text-ink-500">
                      ${(Number(p.perIncidentLimitCents) / 100).toLocaleString()}{" "}
                      per incident · {p.effectiveDate} → {p.expirationDate}
                    </div>
                    {expired ? (
                      <div className="text-[11.5px] text-status-rejected-fg mt-1 font-medium">
                        Expired {Math.abs(daysToExpiry)} days ago — the
                        $100/mo missing-insurance fine begins on the 15th of
                        the month following expiration.
                      </div>
                    ) : expiringSoon ? (
                      <div className="text-[11.5px] text-status-review-fg mt-1 font-medium">
                        Expires in {daysToExpiry} day
                        {daysToExpiry === 1 ? "" : "s"} — renew now to avoid
                        the missing-insurance fine.
                      </div>
                    ) : null}
                    {!expired && p.validationStatus === "pending" && (
                      <div className="text-[11.5px] text-status-review-fg mt-1">
                        Pending Property Management review — you don't need
                        to do anything else. Project submission unlocks once
                        a PM approves this certificate.
                      </div>
                    )}
                    {p.validationStatus === "failed" && (
                      <div className="text-[11.5px] text-status-rejected-fg mt-1 font-medium">
                        Property Management rejected this certificate.
                        Upload a corrected one below.
                      </div>
                    )}
                  </div>
                  {(expired || expiringSoon) && (
                    <span
                      className={cx(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold",
                        expired
                          ? "bg-status-rejected-bg text-status-rejected-fg"
                          : "bg-status-review-bg text-status-review-fg",
                      )}
                    >
                      {expired
                        ? `${Math.abs(daysToExpiry)}d expired`
                        : `${daysToExpiry}d left`}
                    </span>
                  )}
                  <span
                    className={cx(
                      "badge",
                      expired
                        ? "bg-status-rejected-bg text-status-rejected-fg"
                        : p.validationStatus === "passed"
                          ? "bg-status-approved-bg text-status-approved-fg"
                          : "bg-status-submitted-bg text-status-submitted-fg",
                    )}
                  >
                    {expired ? "expired" : p.validationStatus}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(p)}
                      disabled={rowBusyId === p.id}
                      aria-label={`Edit ${p.carrierName} ${p.policyNumber}`}
                      title="Edit policy"
                    >
                      <Pencil size={14} />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void removePolicy(p)}
                      disabled={rowBusyId === p.id}
                      loading={rowBusyId === p.id}
                      aria-label={`Delete ${p.carrierName} ${p.policyNumber}`}
                      title="Delete policy"
                      className="text-status-rejected-fg hover:bg-status-rejected-bg"
                    >
                      <Trash2 size={14} />
                      Delete
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="text-ink-800 m-0 mb-3">
          {editingId ? "Edit policy" : "Add / renew policy"}
        </h2>
        <form onSubmit={submit}>
          <Field
            label="Unit"
            hint={
              editingId
                ? "Moving a policy to a different unit isn't supported — delete this entry and add a fresh one."
                : units.length === 0
                  ? "You're not currently listed as an owner on any unit. Ask Property Management to update your unit assignment."
                  : undefined
            }
          >
            <select
              className="select"
              value={form.unitId}
              onChange={(e) => setForm({ ...form, unitId: e.target.value })}
              required
              disabled={units.length === 0 || editingId !== null}
            >
              <option value="">Choose your unit…</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  Unit {u.unitNumber}
                  {u.buildingName ? ` · ${u.buildingName}` : ""}
                </option>
              ))}
            </select>
          </Field>
          <div className="field-row">
            <Field label="Carrier">
              <input
                className="input"
                value={form.carrierName}
                onChange={(e) =>
                  setForm({ ...form, carrierName: e.target.value })
                }
                required
              />
            </Field>
            <Field label="Policy number">
              <input
                className="input"
                value={form.policyNumber}
                onChange={(e) =>
                  setForm({ ...form, policyNumber: e.target.value })
                }
                required
              />
            </Field>
          </div>
          <Field
            label="Per-incident limit (USD)"
            hint="Minimum $500,000"
          >
            <input
              className="input"
              type="number"
              min={500000}
              step={1000}
              value={form.limitDollars}
              onChange={(e) =>
                setForm({ ...form, limitDollars: e.target.value })
              }
              required
            />
          </Field>
          <div className="field-row">
            <Field label="Effective date">
              <input
                className="input"
                type="date"
                value={form.effectiveDate}
                onChange={(e) =>
                  setForm({ ...form, effectiveDate: e.target.value })
                }
                required
              />
            </Field>
            <Field label="Expiration date">
              <input
                className="input"
                type="date"
                value={form.expirationDate}
                onChange={(e) =>
                  setForm({ ...form, expirationDate: e.target.value })
                }
                required
              />
            </Field>
          </div>
          {!editingId && (
            <Field label="Certificate" hint="PDF or image; optional">
              <input
                className="input"
                type="file"
                name="file"
                accept="application/pdf,image/*"
              />
            </Field>
          )}
          {error && <div className="alert-error">{error}</div>}
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              variant="primary"
              loading={busy}
              disabled={!form.unitId}
            >
              {editingId ? <Save size={16} /> : <Upload size={16} />}
              {busy
                ? "Saving…"
                : editingId
                  ? "Save changes"
                  : "Save policy"}
            </Button>
            {editingId && (
              <Button
                type="button"
                variant="ghost"
                onClick={resetForm}
                disabled={busy}
              >
                <X size={16} />
                Cancel
              </Button>
            )}
          </div>
        </form>
      </Card>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Renewal nudge — surfaces the same data the daily scheduler uses to
// email owners 30 days before expiration. Lists upcoming renewals
// (or expired uncovered units), the COI requirements your carrier
// needs to put on the certificate, the carriers other Elm owners
// use, and a copy-pasteable renewal-request letter.
// ---------------------------------------------------------------------------
function RenewalNudge({ renewal }: { renewal: RenewalView | null }) {
  const [openDraftFor, setOpenDraftFor] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  if (!renewal) return null;
  if (renewal.upcomingRenewals.length === 0) return null;

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      // Best-effort.
    }
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <Sparkles size={18} className="text-brand-700" />
        Insurance renewal nudge
      </h2>
      <p className="text-[13px] text-ink-600 mb-3">
        Renewals coming up in the next 60 days, plus the COI requirements
        your carrier needs to put on the certificate. Elm can also draft
        the renewal request for you.
      </p>

      <ul className="divide-y divide-sand-100 mb-4">
        {renewal.upcomingRenewals.map((r) => (
          <li key={r.unitId} className="py-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-ink-800">
                  Unit {r.unitNumber}
                  <span className="ml-2 text-[12px] font-normal text-ink-500">
                    {r.buildingName}
                  </span>
                </div>
                {r.policyId ? (
                  <div className="text-[12.5px] text-ink-600 mt-0.5">
                    {r.carrierName} · expires {r.expirationDate}
                    {r.daysUntilExpiration !== null && (
                      <span
                        className={cx(
                          "ml-2 font-medium",
                          r.daysUntilExpiration < 0
                            ? "text-status-rejected-fg"
                            : r.daysUntilExpiration <= 14
                              ? "text-status-rejected-fg"
                              : "text-status-review-fg",
                        )}
                      >
                        {r.daysUntilExpiration < 0
                          ? `${Math.abs(r.daysUntilExpiration)}d expired`
                          : `${r.daysUntilExpiration}d left`}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-[12.5px] text-status-rejected-fg mt-0.5">
                    No policy on file. Add one to avoid the missing-insurance
                    fine.
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={openDraftFor === r.unitId ? "primary" : "secondary"}
                  onClick={() =>
                    setOpenDraftFor(
                      openDraftFor === r.unitId ? null : r.unitId,
                    )
                  }
                >
                  <Mail size={13} />
                  {openDraftFor === r.unitId ? "Hide draft" : "Renewal draft"}
                </Button>
              </div>
            </div>
            {openDraftFor === r.unitId && (
              <div className="mt-3 bg-sand-50 border border-sand-100 rounded-md p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500">
                    Email to your carrier
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      void copy(r.renewalDraft, r.unitId)
                    }
                    className="inline-flex items-center gap-1 text-[12px] text-brand-700 hover:text-brand-800 px-2 py-1 rounded hover:bg-brand-50"
                  >
                    <ClipboardCopy size={13} />
                    {copied === r.unitId ? "Copied" : "Copy"}
                  </button>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-[11.5px] text-ink-700 leading-relaxed">
                  {r.renewalDraft}
                </pre>
              </div>
            )}
          </li>
        ))}
      </ul>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-sand-50 border border-sand-100 rounded-md p-3">
          <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500 mb-2">
            COI requirements
          </div>
          <ul className="text-[12.5px] text-ink-700 space-y-1 list-disc pl-4 leading-snug">
            {renewal.coiRequirements.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
        <div className="bg-sand-50 border border-sand-100 rounded-md p-3">
          <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500 mb-2 inline-flex items-center gap-1">
            <Users size={11} />
            Carriers other owners use
          </div>
          {renewal.carrierSuggestions.length === 0 ? (
            <p className="text-[12.5px] text-ink-500 leading-snug">
              No carrier data from neighbors yet. Choose any insurer that
              meets the COI requirements above.
            </p>
          ) : (
            <ul className="text-[12.5px] text-ink-700 space-y-1 leading-snug">
              {renewal.carrierSuggestions.map((c) => (
                <li
                  key={c.carrierName}
                  className="flex items-center justify-between gap-2"
                >
                  <span>{c.carrierName}</span>
                  <span className="text-[11px] text-ink-400">
                    {c.policyCount} policy
                    {c.policyCount === 1 ? "" : "ies"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
